import { useCallback, useState, useMemo } from 'react';

import {
  PlatformTableFilterModeParameter,
  usePlatformTableListColumnsTeam,
} from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import { FieldOption } from 'shared/base';

import { useTableStateContext } from './useTableStateContext';

/**
 * Hook that fetches possible values for a given field based on internal state.
 * Provides a function to update the fieldName and inputText criteria.
 * @returns An object containing the suggestion options, loading/error states, and the update function.
 */
export const useGetFieldOptions = () => {
  const { currentMembership } = useBaseAuthenticated();
  const { tableName } = useTableStateContext();
  const [currentInputText, setCurrentInputText] = useState<string | undefined>(undefined);
  // Prepare params for the query hook
  const queryParams = useMemo(
    () => ({
      ...(currentInputText?.trim().length
        ? {
            filter: `${currentInputText.trim()}*`,
          }
        : { filter_mode: PlatformTableFilterModeParameter.column }), // Speedier and floats common fields to top
      per_page: 1000,
    }),
    [currentInputText],
  );

  // Call the hook at the top level, driven by state
  const { data, isError, isFetching, isLoading } = usePlatformTableListColumnsTeam(
    currentMembership.team.name,
    tableName,
    {
      filter_mode: PlatformTableFilterModeParameter.column,
      per_page: queryParams.per_page,
    },
    {
      query: {
        enabled: !!tableName,
        keepPreviousData: true, // Optionally keep stale data while loading new field/text
      },
    },
  );

  // Function to trigger an update by setting the state
  const updateCriteria = useCallback((inputText?: string) => {
    setCurrentInputText(inputText);
  }, []);

  // Memoize the transformation of the raw data into ValueOption[]
  const options = useMemo((): FieldOption[] => {
    if (isLoading || isError || !data?.data?.items) {
      return []; // Return empty array while loading, on error, or if no data
    }

    // Process the fetched column value data
    return data.data.items.map((item) => {
      if (item.kind === 'map' || item.kind === 'array') {
        return {
          ...item,
          name: `${item.name}.*`,
        };
      }

      return item;
    });
  }, [data, isLoading, isError]);

  return {
    // Rename the function to reflect its action
    fieldOptions: options,
    isError,
    isLoading: isLoading || isFetching,
    updateFieldOptions: updateCriteria,
  };
};
